import type { StateCreator } from 'zustand';
import type {
  DataProcessorSlice,
  DefinitionFormStoreState,
} from '../types/definitionFormStoreTypes';
import { DEFAULT_UNIVERSE_ID } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/StartingUniverse/config/constants/staticUniverses';

// Exported so the config-adapter test can assert Step 1's submitted
// `eligible_universe` against the REAL fresh-form default — not a hand-rolled
// copy that can silently drift from this source of truth.
export const getInitialValues = () => {
  return {
    universe: {
      type: 'index' as const,
      portfolio_id: DEFAULT_UNIVERSE_ID,
      geographic_scope: ['north_america'],
      cyclicality: ['cyclical', 'defensive', 'sensitive'],
      size_segment: ['large', 'mid', 'small'],
    },
    primary_share: false,
    exclude_type: {
      common: false,
      preferred: false,
      reit: false,
      etf: false,
      Adr: false,
      right: false,
      warrants: false,
      units: false,
      tracking_stock: false,
      closed_end_fund: false,
    },
    exclude_gics_sector: [],
    exclude_gics_industries: [],
    exclude_gics_sub_industries: [],
    exclude_countries: [],
    exclude_exchanges: [],
    liquidity: {
      price_range: {
        min: 0,
        max: Number.MAX_SAFE_INTEGER,
      },
      avg_dollar_volume: {
        min: 0,
        max: Number.MAX_SAFE_INTEGER,
      },
      float: {
        min: 0,
        // Inactive default is the UNCAPPED sentinel, like every other filter —
        // NOT 100. The float axis ceiling ceils slightly above 100, so a
        // default of 100 reads as an active cap and opens the filter on a fresh
        // draft. valueMax={100} still caps what the user can type; the adapter
        // treats >= 100% as "no cap" either way.
        max: Number.MAX_SAFE_INTEGER,
      },
    },
    size: {
      min: 0,
      max: Number.MAX_SAFE_INTEGER,
    },
    history: {
      min: 0,
      max: Number.MAX_SAFE_INTEGER,
    },
    esg_filters: {
      min: 0,
    },
    single_stock_restriction: {
      blocked: [],
    },
    keyword_exclusions: [],
    benchmark: ['SPY'],

    metrics_aggregator: [],
    constituents_fields: ['market_cap', 'sector_id'],
  };
};

export const createDataProcessorSlice: StateCreator<
  DefinitionFormStoreState,
  [['zustand/immer', never]],
  [],
  DataProcessorSlice
> = (set) => ({
  data_processor: getInitialValues(),

  setUniverse: (universe) =>
    set((state) => {
      Object.assign(state.data_processor.universe, universe);
    }),

  setPrimaryShare: (enabled) =>
    set((state) => {
      state.data_processor.primary_share = enabled;
    }),

  setExcludeType: (excludeType) =>
    set((state) => {
      Object.assign(state.data_processor.exclude_type, excludeType);
    }),

  setExcludeCountries: (countries) =>
    set((state) => {
      state.data_processor.exclude_countries = countries;
    }),

  setExcludeExchanges: (exchanges) =>
    set((state) => {
      state.data_processor.exclude_exchanges = exchanges;
    }),

  setExcludeGicsSector: (sectors) =>
    set((state) => {
      state.data_processor.exclude_gics_sector = sectors;
    }),

  setExcludeGicsIndustries: (industries) =>
    set((state) => {
      state.data_processor.exclude_gics_industries = industries;
    }),

  setExcludeGicsSubIndustries: (subIndustries) =>
    set((state) => {
      state.data_processor.exclude_gics_sub_industries = subIndustries;
    }),

  setLiquidity: (liquidity) =>
    set((state) => {
      Object.assign(state.data_processor.liquidity, liquidity);
    }),

  setSize: (size) =>
    set((state) => {
      Object.assign(state.data_processor.size, size);
    }),

  setHistory: (history) =>
    set((state) => {
      Object.assign(state.data_processor.history, history);
    }),

  setEsgFilters: (esg) =>
    set((state) => {
      Object.assign(state.data_processor.esg_filters, esg);
    }),

  setSingleStockRestriction: (restriction) =>
    set((state) => {
      Object.assign(state.data_processor.single_stock_restriction, restriction);
    }),

  setKeywordExclusions: (keywords) =>
    set((state) => {
      state.data_processor.keyword_exclusions = keywords;
    }),

  setBenchmark: (benchmark) =>
    set((state) => {
      state.data_processor.benchmark = benchmark;
    }),

  addMetricAggregator: (item) =>
    set((state) => {
      state.data_processor.metrics_aggregator.push(item);
    }),

  removeMetricAggregator: (index) =>
    set((state) => {
      state.data_processor.metrics_aggregator.splice(index, 1);
    }),

  setConstituentsFields: (constituents_fields) =>
    set((state) => {
      state.data_processor.constituents_fields = constituents_fields;
    }),
});
